import { inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Static } from '$core/models/static';
import { StaticDataUpdateDto, transformStaticDataDtoToModel } from './types';

@Injectable({
  providedIn: 'root',
})
export class StaticStorage {
  private readonly translateService = inject(TranslateService);

  private readonly storage = signal<Static>(<Static>{});

  public readonly static = this.storage.asReadonly();

  /**
   * Emits after the language is applied, i.e. requests already go with the new `hl` header.
   */
  public readonly langChange$ =
    this.translateService.onLangChange.asObservable();

  /**
   * Language each static key was loaded in.
   */
  private readonly keyLangs = new Map<string, string>();

  public updateStatic(data: StaticDataUpdateDto): void {
    const dataModel = transformStaticDataDtoToModel(data);

    Object.keys(dataModel).forEach((key) =>
      this.keyLangs.set(key, this.translateService.currentLang)
    );

    this.storage.set({
      ...this.static(),
      ...dataModel,
    });
  }

  /**
   * Checks that all keys are cached and loaded in the current language.
   */
  public hasActual(...keys: (keyof Static)[]): boolean {
    return keys.every(
      (key) =>
        !!this.static()[key] &&
        this.keyLangs.get(key) === this.translateService.currentLang
    );
  }
}
